import { useCallback, useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useCognitoAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/hooks/useOrganization';
import { api } from '@/lib/api';
import type { BeangoError } from '@/lib/beango-error';
import { ErrorCode, userMessage } from '@/lib/error-codes';

export function FinancialSettings() {
  const { organization, refreshOrganizations } = useOrganization();
  const { idToken } = useCognitoAuth();

  const [startDate, setStartDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!organization) return;
    let date = '';
    try {
      if (organization.settings) {
        const parsed = JSON.parse(organization.settings) as Record<string, unknown>;
        date = (parsed.start_date as string) ?? '';
      }
    } catch {
      // settings is not valid JSON, leave blank
    }
    setStartDate(date);
  }, [organization]);

  const handleSave = useCallback(async () => {
    if (!organization) return;
    setError(null);
    setSaved(false);
    setSaving(true);
    try {
      const settingsObj: Record<string, string> = {};
      if (startDate) {
        settingsObj.start_date = startDate;
      }
      const settings = JSON.stringify(settingsObj);
      await api.patch(`/api/organizations/${organization.id}`, { settings }, { errorCode: ErrorCode.SAVE_FINANCIAL_SETTINGS, token: idToken! });
      setSaved(true);
      await refreshOrganizations();
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err instanceof Error && 'code' in err ? userMessage((err as BeangoError).code) : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }, [organization, startDate, refreshOrganizations, idToken]);

  if (!organization) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-muted-foreground">No organization selected.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Financial Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Configure financial defaults for your organization including historical data boundaries.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Business Start Date</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Sets the earliest date for which sales data will be imported from your connected stores.
            Orders before this date will not be fetched during initial or manual syncs.
            If left empty, the system defaults to the last 60 days.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="flex-1"
              max={new Date().toISOString().split('T')[0]}
            />
            <Button onClick={() => void handleSave()} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          {saved && <p className="text-sm text-green-600">Start date saved.</p>}
        </CardContent>
      </Card>
    </div>
  );
}
